import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ShieldAlert, Plus, Filter, TrendingUp, AlertTriangle, CheckCircle } from 'lucide-react'
import api from '../../services/api'

const RATING_COLOURS: Record<string, { bg: string; text: string; border: string }> = {
  critical: { bg: '#FEF2F2', text: '#DC2626', border: '#FECACA' },
  high:     { bg: '#FFF7ED', text: '#EA580C', border: '#FED7AA' },
  medium:   { bg: '#FEFCE8', text: '#CA8A04', border: '#FEF08A' },
  low:      { bg: '#F0FDF4', text: '#16A34A', border: '#BBF7D0' },
}

const CATEGORIES = ['operational','health_safety','environmental','financial','reputational','compliance','strategic']

export default function RiskRegisterList() {
  const navigate = useNavigate()
  const [risks, setRisks] = useState<any[]>([])
  const [summary, setSummary] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState({ status: '', category: '', rating: '' })

  const load = async () => {
    const params = new URLSearchParams()
    if (filters.status)   params.set('status', filters.status)
    if (filters.category) params.set('category', filters.category)
    if (filters.rating)   params.set('rating', filters.rating)
    const [list, sum] = await Promise.all([
      api.get(`/risk-register?${params}&limit=100`),
      api.get('/risk-register/summary'),
    ])
    setRisks(list.data.data)
    setSummary(sum.data)
    setLoading(false)
  }

  useEffect(() => { load() }, [filters])

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>Risk Register</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>Identify, assess and control workplace risks</p>
        </div>
        <button onClick={() => navigate('/risk-register/new')} className="btn btn-primary">
          <Plus size={16} /> Add Risk
        </button>
      </div>

      {/* Summary matrix */}
      {summary && (
        <div className="grid grid-cols-4 gap-3">
          {(['critical','high','medium','low'] as const).map(r => {
            const c = RATING_COLOURS[r]
            return (
              <button key={r} onClick={() => setFilters(f => ({ ...f, rating: f.rating === r ? '' : r }))}
                className="card text-center cursor-pointer transition-all hover:shadow-md"
                style={{ borderColor: filters.rating === r ? c.border : 'var(--border)', borderWidth: 2 }}>
                <div className="text-3xl font-extrabold" style={{ color: c.text }}>{summary[r]}</div>
                <div className="text-xs font-semibold uppercase mt-1" style={{ color: c.text }}>{r}</div>
              </button>
            )
          })}
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
          className="input text-sm" style={{ width: 'auto' }}>
          <option value="">All statuses</option>
          <option value="open">Open</option>
          <option value="in_treatment">In Treatment</option>
          <option value="accepted">Accepted</option>
          <option value="closed">Closed</option>
        </select>
        <select value={filters.category} onChange={e => setFilters(f => ({ ...f, category: e.target.value }))}
          className="input text-sm" style={{ width: 'auto' }}>
          <option value="">All categories</option>
          {CATEGORIES.map(c => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
        </select>
        {(filters.status || filters.category || filters.rating) && (
          <button onClick={() => setFilters({ status: '', category: '', rating: '' })}
            className="text-sm font-medium" style={{ color: 'var(--accent)' }}>Clear filters</button>
        )}
      </div>

      {/* Table */}
      <div className="card p-0 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--accent)' }} />
          </div>
        ) : risks.length === 0 ? (
          <div className="text-center py-16">
            <ShieldAlert size={40} style={{ color: 'var(--text-muted)', margin: '0 auto 12px' }} />
            <p className="font-semibold" style={{ color: 'var(--text)' }}>No risks found</p>
            <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>Add your first risk to start building your register</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-subtle)' }}>
                {['Risk', 'Category', 'Inherent', 'Residual', 'Owner', 'Review', 'Status'].map(h => (
                  <th key={h} className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wide"
                    style={{ color: 'var(--text-muted)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {risks.map(risk => {
                const ic = RATING_COLOURS[risk.risk_rating] || RATING_COLOURS.low
                const rc = RATING_COLOURS[risk.residual_rating] || RATING_COLOURS.low
                return (
                  <tr key={risk.id} onClick={() => navigate(`/risk-register/${risk.id}`)}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                    style={{ borderBottom: '1px solid var(--border)' }}>
                    <td className="px-4 py-3">
                      <div className="font-medium" style={{ color: 'var(--text)' }}>{risk.title}</div>
                      <div className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{risk.risk_number}</div>
                    </td>
                    <td className="px-4 py-3 capitalize" style={{ color: 'var(--text-muted)' }}>
                      {risk.category?.replace(/_/g, ' ')}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold"
                        style={{ background: ic.bg, color: ic.text, border: `1px solid ${ic.border}` }}>
                        {risk.likelihood}×{risk.consequence} {risk.risk_rating}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold"
                        style={{ background: rc.bg, color: rc.text, border: `1px solid ${rc.border}` }}>
                        {risk.residual_likelihood || '—'}×{risk.residual_consequence || '—'} {risk.residual_rating}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm" style={{ color: 'var(--text-muted)' }}>{risk.owner_name || '—'}</td>
                    <td className="px-4 py-3 text-sm" style={{ color: risk.review_date && new Date(risk.review_date) < new Date() ? 'var(--red)' : 'var(--text-muted)' }}>
                      {risk.review_date ? new Date(risk.review_date).toLocaleDateString('en-AU') : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 rounded-full text-xs font-semibold capitalize"
                        style={{ background: risk.status === 'closed' ? 'var(--green-light)' : 'var(--accent-light)', color: risk.status === 'closed' ? 'var(--green)' : 'var(--accent)' }}>
                        {risk.status?.replace(/_/g, ' ')}
                      </span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
